import type { TbLock } from "solid-icons/tb";
import { For, type JSX, type ParentComponent, Show } from "solid-js";
import type { Logo } from "~/components/marketing/content";
import { LogoMark } from "~/components/marketing/LogoMark";
import { CardArrow, Eyebrow, LINK_CARD, SectionTitle } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import "~/components/marketing/mockups/mockups.css";

/**
 * One shipped detail. `color` is the hue of its icon tile; `logos` or `chips`
 * show the brands or values it names; `wide` spans two columns for the one or
 * two details a group leads with; `href` is where the site or the help docs
 * cover it further, opened in a new tab when it leaves the site, and a detail
 * nothing covers further is a plain card.
 */
type Detail = {
  icon: typeof TbLock;
  color: string;
  title: TranslationKey;
  body: TranslationKey;
  href?: string;
  logos?: Logo[];
  chips?: string[];
  wide?: boolean;
};

const CARD =
  "relative flex h-full flex-col gap-3 overflow-hidden rounded-2xl border border-primary-border bg-tile p-5 sm:p-6";

/**
 * One capability group: the anchor the homepage links to, its headline and
 * promise, the product doing it at full width, and the shipped details under
 * it as a bento. Children render after the details.
 */
export const FeatureGroup: ParentComponent<{
  id: string;
  index: string;
  eyebrow: string;
  title: string;
  body: string;
  visual: JSX.Element;
  details: Detail[];
}> = (props) => {
  // Four across only when a row of four fills it; wide details need the three-column rhythm.
  const fourUp = () => props.details.length % 4 === 0 && !props.details.some((detail) => detail.wide);
  return (
    <section
      id={props.id}
      aria-labelledby={`${props.id}-title`}
      class="scroll-mt-32 border-t border-primary-border px-4 py-20 sm:px-6 sm:py-24 lg:px-8"
    >
      <div class="mx-auto max-w-7xl">
        <div class="grid gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-end lg:gap-16">
          <div class="flex flex-col gap-5">
            <Eyebrow index={props.index} label={props.eyebrow} id={props.id} />
            <SectionTitle id={`${props.id}-title`} text={props.title} />
          </div>
          <p class="max-w-xl text-lg leading-relaxed text-secondary-text">{props.body}</p>
        </div>
        <div class="mt-12">{props.visual}</div>
        <ul
          class="mt-12 grid gap-4 sm:grid-cols-2"
          classList={{ "lg:grid-cols-4": fourUp(), "lg:grid-cols-3": !fourUp() }}
        >
          <For each={props.details}>
            {(detail) => (
              <li classList={{ "sm:col-span-2": detail.wide }} style={{ "--c": detail.color }}>
                <Show
                  when={detail.href}
                  fallback={
                    <div class={CARD}>
                      <DetailContent detail={detail} />
                    </div>
                  }
                >
                  {(href) => (
                    <a
                      href={href()}
                      target={href().startsWith("http") ? "_blank" : undefined}
                      rel={href().startsWith("http") ? "noopener noreferrer" : undefined}
                      class={`${CARD} ${LINK_CARD}`}
                    >
                      <DetailContent detail={detail} />
                    </a>
                  )}
                </Show>
              </li>
            )}
          </For>
        </ul>
        {props.children}
      </div>
    </section>
  );
};

const DetailContent = (props: { detail: Detail }) => {
  const { t } = useI18n();
  return (
    <>
      <span class="mk-ink relative grid size-10 shrink-0 place-items-center rounded-xl bg-(--c)/12 ring-1 ring-(--c)/20 dark:bg-(--c)/20">
        <props.detail.icon class="size-5" aria-hidden="true" />
      </span>
      <h3 class="relative mt-1 flex items-center gap-1.5 font-semibold">
        {t(props.detail.title)}
        <Show when={props.detail.href}>
          <CardArrow />
        </Show>
      </h3>
      <p class="relative -mt-1 text-sm leading-relaxed text-secondary-text">{t(props.detail.body)}</p>
      <Show when={props.detail.logos}>
        {(logos) => (
          <ul class="relative mt-auto flex flex-wrap gap-1.5 pt-2">
            <For each={logos()}>
              {(logo) => (
                <li
                  title={logo.name}
                  class="grid size-9 place-items-center rounded-xl border border-primary-border bg-canvas shadow-sm"
                >
                  <LogoMark mark={logo.mark} class="size-5" />
                  <span class="sr-only">{logo.name}</span>
                </li>
              )}
            </For>
          </ul>
        )}
      </Show>
      <Show when={props.detail.chips}>
        {(chips) => (
          <ul class="relative mt-auto flex flex-wrap gap-1.5 pt-2" aria-hidden="true">
            <For each={chips()}>
              {(chip) => (
                <li class="rounded-md bg-(--c)/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-primary-text ring-1 ring-(--c)/20">
                  {chip}
                </li>
              )}
            </For>
          </ul>
        )}
      </Show>
    </>
  );
};
